import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import * as Yup from "yup";

import MainContainer from "../../../layout/MainContainer";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { isValidInputWithSC } from "../../../utils/validations";
import {
  AddPost,
  DeletePost,
  GetCategories,
  GetPostById,
  GetUser,
  UpdatePost,
} from "../../../services/home";
import { Loader, showError, showToast } from "../../../constants";
import { AuthorOption, Post } from "../../../types/General";
import CloseIcon from "@mui/icons-material/Close";
import { AuthorSelect, PostFormActions, QuillEditor, WarnModal } from "../../../components";
import { createDownscaledPreviewUrl } from "../../../utils/images";
const VideoForm = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEdit = Boolean(postId);

  const [video, setVideo] = useState<File | string>("");
  const [videoPreview, setVideoPreview] = useState("");
  const [thumbnail, setThumbnail] = useState<File | string>("");
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [headline, setHeadline] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("Published");
  const [otherDetails, setOtherDetails] = useState<Post | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState<AuthorOption | null>(null);
  const [allCategories, setAllCategories] = useState<{ _id: string; name: string; parentCategoryId?: string | null }[]>([]);
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [postCategoryId, setPostCategoryId] = useState<string>("");

  const parentCategoryOptions = allCategories.filter(cat => !cat.parentCategoryId);
  const childCategoryOptions = selectedParentCategoryId
    ? allCategories.filter(cat => cat.parentCategoryId === selectedParentCategoryId)
    : [];
  const hasSubcategories = childCategoryOptions.length > 0;

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      headlineText: headline || "",
      // description: description || "",
    },
    validationSchema: Yup.object({
      headlineText: Yup.string()
        .required("Headline text is required")
        .max(200, "Maximum 200 character are allowed"),
      // description: Yup.string()
      //   .required("Description is required")
      //   .max(1000, "Maximum 1000 character are allowed"),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);

      if (!videoPreview?.length) {
        showError("Video is required");
        return;
      }
      submitVideos(status);
    },
  });

  const submitVideos = async (status:any) => {
    if (!selectedParentCategoryId) {
      showError("Please select a parent category");
      return;
    }
    if (status === "Published" && hasSubcategories && !selectedCategoryId) {
      showError("Please select a subcategory to publish");
      return;
    }
    if (status === "Published" && !selectedAuthor) {
      showError("Please select an author to publish");
      return;
    }

    const formData = new FormData();

    formData.append("video", video);
    formData.append("thumbnail", thumbnail);
    formData.append("description", description);
    formData.append("synopsis", synopsis.trim());
    formData.append("title", formik.values.headlineText);
    formData.append("categoryId", selectedCategoryId || selectedParentCategoryId);
    formData.append("contentType", "Video");
    formData.append("status", status);
    if (selectedAuthor) {
      const authorName = [selectedAuthor.firstName, selectedAuthor.lastName].filter(Boolean).join(" ") || selectedAuthor.username;
      formData.append("author", authorName);
      formData.append("userId", selectedAuthor._id);
    }

    setLoading(true);

    try {
      let response: any;
      let status = "";
      if (postId) {
        response = (await UpdatePost(formData, postId)) as {
          status: number;
        };
        status = "updated";
      } else {
        response = (await AddPost(formData)) as { status: number };
        status = "created";
      }
      setLoading(false);
      if (response?.status === 201) {
        showToast(`Video ${status} successfully`);
        navigate(-1);
      }
    } catch (error: any) {
      setLoading(false);
      console.log(error);
    }
  };

  function createObjectURL(object: File) {
    return window.URL
      ? window.URL.createObjectURL(object)
      : window.webkitURL.createObjectURL(object);
  }

  const handleThumbnailUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event?.target?.files?.length) return;
    const file = event.target.files[0];
    if (!file?.type.includes("image")) {
      showError("Only image types are allowed");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const image = new Image();
      image.src = ev?.target?.result as string;
      image.onload = async () => {
        if (image.width < 1920 || image.height < 1080) {
          showError(
            `Image must be 1920x1080 resolution. This image has ${image.width}x${image.height} resolution`
          );
          return;
        }
        try {
          const previewUrl = await createDownscaledPreviewUrl(image);
          setThumbnailPreview(previewUrl);
          setThumbnail(file);
        } catch (e) {
          console.log("Preview downscale failed, falling back to original:", e);
          setThumbnailPreview(URL.createObjectURL(file));
          setThumbnail(file);
        }
      };
    };
    reader.readAsDataURL(file);
  };

  const handleVideoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event?.target?.files?.length) {
      const file = event?.target?.files[0];

      if (file && file.type.includes("video")) {
        let url = createObjectURL(file);
        setVideoPreview(url);
        setVideo(file);
      } else {
        showError("Only video types are allowed");
      }
    }
  };

  const getPostDetails = useCallback(async () => {
    if (!postId) {
      return;
    }
    try {
      setLoading(true);

      const response = (await GetPostById({ postId })) as {
        data: Post;
        code: number;
      };
      setLoading(false);

      if (response?.code === 200) {
        setHeadline(response?.data?.title || "");
        setDescription(response?.data?.description || "");
        setSynopsis(response?.data?.synopsis || "");
        setVideo(response?.data?.video || "");
        setVideoPreview(response?.data?.video || "");
        setThumbnail(response?.data?.thumbnail || "");
        setThumbnailPreview(response?.data?.thumbnail || "");
        setOtherDetails(response?.data || null);
        if (response?.data?.categoryId) setPostCategoryId(response.data.categoryId);

        // Pre-populate author if a specific user was set
        if (response?.data?.userId && response?.data?.author) {
          let loaded = false;
          try {
            const userResponse = (await GetUser(response.data.userId)) as { data: any; code: number };
            if (userResponse?.code === 200 && userResponse?.data) {
              const userData = Array.isArray(userResponse.data) ? userResponse.data[0] : userResponse.data;
              if (userData) {
                setSelectedAuthor({
                  _id: userData._id,
                  username: userData.username,
                  email: userData.email,
                  profileImage: userData.profileImage,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                });
                loaded = true;
              }
            }
          } catch (e) {
            console.log("Failed to load author details:", e);
          }
          if (!loaded) {
            setSelectedAuthor({
              _id: response.data.userId,
              username: response.data.author,
              email: "",
              profileImage: "",
              firstName: response.data.author,
              lastName: "",
            });
          }
        }
      }
    } catch (error) {
      setLoading(false);

      console.log(error);
    }
  }, [postId]);

  const discardPost = async () => {
    try {
      if (!postId) {
        return;
      }
      setLoading(true);

      const response = (await DeletePost(postId)) as { status: number };
      setLoading(false);

      if (response?.status === 200) {
        showToast(`Video deleted successfully`);
        navigate(-1);
      }
    } catch (error) {
      setLoading(false);

      console.log(error);
    }
  };

  useEffect(() => {
    GetCategories().then((res: any) => {
      if (res?.code === 200) setAllCategories(res.data || []);
    });
    if (isEdit) {
      getPostDetails();
    }
  }, [isEdit, getPostDetails]);

  // Resolve parent + child category when editing an existing video
  useEffect(() => {
    if (!postCategoryId || !allCategories.length) return;
    const category = allCategories.find(cat => cat._id === postCategoryId);
    if (category?.parentCategoryId) {
      setSelectedParentCategoryId(category.parentCategoryId);
      setSelectedCategoryId(category._id);
    } else if (category) {
      setSelectedParentCategoryId(category._id);
      setSelectedCategoryId("");
    }
  }, [postCategoryId, allCategories]);

  return (
    <>
      <Loader isLoad={loading} />
      <MainContainer>
        <form onSubmit={formik.handleSubmit}>
          <div className="main_loyout">
            <div className="dashboard article-dash">
              <h1 className="mn_hdng">
                {isEdit ? "Edit Video" : "Add Video"}
              </h1>
              <Button className="btn btn_primary" onClick={() => navigate(-1)}>
               Go Back
              </Button>
            </div>
            <Card className="cards article-page">
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Parent Category</InputLabel>
                    <Select
                      value={selectedParentCategoryId}
                      label="Parent Category"
                      onChange={(e) => {
                        setSelectedParentCategoryId(e.target.value);
                        setSelectedCategoryId("");
                      }}
                    >
                      <MenuItem value="">Select parent category</MenuItem>
                      {parentCategoryOptions.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!selectedParentCategoryId || !hasSubcategories}>
                    <InputLabel>
                      {!selectedParentCategoryId
                        ? "Select parent category"
                        : hasSubcategories
                          ? "Subcategory"
                          : "No subcategories"}
                    </InputLabel>
                    <Select
                      value={selectedCategoryId}
                      label={
                        !selectedParentCategoryId
                          ? "Select parent category"
                          : hasSubcategories
                            ? "Subcategory"
                            : "No subcategories"
                      }
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                    >
                      <MenuItem value="">Select subcategory</MenuItem>
                      {childCategoryOptions.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <AuthorSelect value={selectedAuthor} onChange={setSelectedAuthor} />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={6} display="flex">
                  <TextField
                    id="headlineText"
                    label="Video headline"
                    multiline
                    className="custom-textarea"
                    type={"text"}
                    name="headlineText"
                    variant="outlined"
                    fullWidth
                    inputProps={{ maxLength: 200 }}
                    placeholder={"Enter video headline..."}
                    onBlur={formik.handleBlur}
                    value={formik.values.headlineText}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isValidInputWithSC(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.headlineText && formik.errors.headlineText
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Synopsis (SEO Meta Description)"
                    multiline
                    className="custom-textarea"
                    type="text"
                    variant="outlined"
                    fullWidth
                    inputProps={{ maxLength: 155 }}
                    placeholder="Brief description for search engine results..."
                    value={synopsis}
                    onChange={(e) => setSynopsis(e.target.value)}
                    helperText={`${synopsis.length}/155 characters`}
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <QuillEditor
                    value={description}
                    onChange={setDescription}
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike", "blockquote"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["clean"],
                      ],
                    }}
                    placeholder="Add video description..."
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  {videoPreview ? (
                    <div className="image-preview video-preview">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setVideo("");
                          setVideoPreview("");
                        }}
                        className="close-icon"
                      >
                        <CloseIcon />
                      </div>
                      <video
                        src={videoPreview}
                        controls
                      />
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      component="label"
                      className="custom-input"
                    >
                      Upload Video
                      <input type="file" hidden onChange={handleVideoUpload} />
                    </Button>
                  )}
                </Grid>

                <Grid item xs={12} lg={6}>
                  {thumbnailPreview ? (
                    <div className="image-preview">
                      <div
                        onClick={() => {
                          setThumbnail("");
                          setThumbnailPreview("");
                        }}
                        className="close-icon"
                      >
                        <CloseIcon />
                      </div>
                      <img src={thumbnailPreview} alt="" />
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      component="label"
                      className="custom-input"
                    >
                      Upload Thumbnail
                      <input
                        type="file"
                        hidden
                        onChange={handleThumbnailUpload}
                      />
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={2}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                >
                  {postId ? (
                    <ul className="footer-list">
                      <li>{otherDetails?.uuid || "N/A"}</li>
                      <li>{otherDetails?.author || "N/A"}</li>
                      <li>{otherDetails?.status || "N/A"}</li>
                    </ul>
                  ) : null}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={{ xs: "center", sm: "flex-end" }}
                  alignItems="center"
                  className="form-actions"
                >
                  <PostFormActions
                    isEdit={isEdit}
                    status={otherDetails?.status}
                    onDiscard={() => setOpen(true)}
                    onDisable={() => setStatus("Disabled")}
                    onSave={() => {
                      setStatus("Draft");
                      submitVideos("Draft");
                    }}
                    onUpdate={() => setStatus(otherDetails?.status || "")}
                    onPublish={() => setStatus("Published")}
                  />
                </Grid>
              </Grid>
            </Card>
          </div>
        </form>
      </MainContainer>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={discardPost}
        name="Video"
      />
    </>
  );
};

export default VideoForm;
